import { animate, style, transition, trigger } from '@angular/animations';
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  Input,
  Output,
  OnChanges,
  SimpleChanges,
  OnInit,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

import { twJoin, twMerge } from '../../utils';
import { generateCustomId } from '@app/shared/utils/custom-id';
import { CdkMenuTrigger, CdkMenu, CdkMenuItem } from '@angular/cdk/menu';
import { IconComponent } from '../../icons/icon.component';
import { TranslateService } from '@ngx-translate/core';

export interface DropdownRowItem {
  id: string | number;
  name: string;
  secondaryName?: string;
  badgeLabel?: string;
  className?: string;
  iconName?: string;
  disabled?: boolean;
  actionItemConfig?: {
    action?: () => void;
    routerLink?: string;
    startIconName?: string;
  };
}

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'da-dropdown',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => DropdownComponent),
    },
  ],
  templateUrl: './dropdown.component.html',
  animations: [
    trigger('PopoverTrigger', [
      transition(':enter', [
        style({ opacity: 0, transform: 'scale(95%)' }),
        animate(
          '200ms ease-out',
          style({ opacity: 1, transform: 'scale(100%)' })
        ),
      ]),
      transition(':leave', [
        style({ opacity: 1, transform: 'scale(100%)' }),
        animate(
          '100ms ease-in',
          style({ opacity: 0, transform: 'scale(95%)' })
        ),
      ]),
    ]),
  ],
  standalone: true,
  imports: [CdkMenuTrigger, IconComponent, CdkMenu, CdkMenuItem],
})
export class DropdownComponent
  implements ControlValueAccessor, OnChanges, OnInit
{
  @Input() public items!: DropdownRowItem[];
  @Input() public multiSelect: boolean = false;
  @Input() public showInitialSelectionWithMultiSelect: boolean = false; // show the first selected item in the dropdown +number of other selections
  @Input() public className?: string;
  @Input() public ariaLabelledby?: string = '';
  @Input() public iconAriaLabel?: string =
    this.translate.instant('Core_MoreOptions');
  @Input() public ariaDescribedby?: string = '';
  @Input() public placeholder?: string;
  @Input() public selectedItem: DropdownRowItem | null = null;
  @Input() public selectedItems: DropdownRowItem[] = [];
  @Input() public iconName: string = 'chevron-down';
  @Input() public autoReset: boolean = false;
  @Input() public disabled: boolean = false;
  @Input() public iconOnly: boolean = false;
  @Input() public showLabel: boolean = true;
  @Input() public showSecondaryNameOnSelection: boolean = true;
  @Output() public itemSelect = new EventEmitter<DropdownRowItem>();
  @Output() public itemsSelected = new EventEmitter<DropdownRowItem[]>();

  public open = false;
  public twJoin = twJoin;
  public twMerge = twMerge;
  public uid = generateCustomId();

  public get isActionMenu() {
    return this.items.some((item) => !!item.actionItemConfig);
  }

  constructor(
    private cdr: ChangeDetectorRef,
    private translate: TranslateService
  ) {}

  public ngOnInit() {
    if (this.iconOnly && this.ariaLabelledby === '') {
      throw new Error('iconAriaLabel required for icon only dropdowns');
    }
  }

  public ngOnChanges(changes: SimpleChanges) {
    if (changes.selectedItem) {
      this.selectedItems = [this.selectedItem];
    }
  }

  public handleItemSelect(item: DropdownRowItem) {
    // since we should always use aria-disabled, we have to
    // handle the disabled state and not do anything on click
    // if the item is disabled
    // also just treat every item as disabled if the menu is disabled
    if (this.disabled || item.disabled) {
      return;
    }

    this.onChange(item);

    if (!this.multiSelect) {
      this.selectedItems = [item];
      this.itemSelect.emit(item);
      this.open = false;
    } else {
      this.selectedItems = this.addOrRemoveItems(this.selectedItems, item);
      this.itemsSelected.emit(this.selectedItems);
    }

    if (this.autoReset) {
      this.selectedItems = [];
    }
  }

  public handleItemClicked(item: DropdownRowItem) {
    if (!item.actionItemConfig) return;

    if (item.actionItemConfig.action) {
      item.actionItemConfig.action();
    }
  }

  public get ariaLabelledbyClasses() {
    let labels = `da-dropdown-label-${this.uid} ${this.ariaLabelledby} `;

    // preventing the placeholder getting read out multiple times
    // by only adding the value in if there's one selected
    if (this.selectedItems.length) {
      labels += `da-dropdown-value-${this.uid} `;
    }

    return labels;
  }

  public isItemSelected(id: string | number): boolean {
    return this.selectedItems?.some(
      (item) => item?.id === id && !item?.actionItemConfig
    );
  }

  public writeValue(item: DropdownRowItem): void {
    this.selectedItems = this.multiSelect
      ? this.addOrRemoveItems(this.selectedItems, item)
      : [item];
    this.onChange(this.selectedItems);
    this.cdr.detectChanges();
  }

  public registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  public addOrRemoveItems(
    items: DropdownRowItem[],
    item: DropdownRowItem
  ): DropdownRowItem[] {
    let arr = [...items];
    const index = arr.findIndex((o) => o.id === item.id);
    if (index !== -1) {
      arr.splice(index, 1);
    } else {
      arr = [...arr, item];
    }
    return arr;
  }

  /*
   * Implement ControlValueAccessor interface methods
   */
  private onChange: (value: any) => void = () => {};
  private onTouched: (value: any) => void = () => {};
}
